import React, { useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/Breadcrumb';
import CouponService from '../../services/CouponService';
import ProductService from '../../services/ProductService';
import CategoryService from '../../services/CategoryService';
import CouponInterface from './CouponInterface';
import AuthenticationService from '../../services/AuthenticationService';

const UpdateCoupon: React.FC = () => {
  const { couponId } = useParams<{ couponId: string }>();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState<CouponInterface | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    // Check if user has admin permissions
    const currentUser = AuthenticationService.getCurrentUser();
    if (!currentUser || (!currentUser.roles?.includes('ROLE_SUPERADMIN') && !currentUser.roles?.includes('ROLE_ADMIN'))) {
      toast.error('You do not have permission to access coupon management.');
      navigate('/coupons');
      return;
    }

    if (couponId) {
      fetchCouponDetails();
      fetchProductsAndCategories();
    }
  }, [couponId, navigate]);

  const fetchCouponDetails = async () => {
    try {
      const response = await CouponService.getCoupon(parseInt(couponId!));
      setCoupon(response.data);
    } catch (error) {
      console.error('Error fetching coupon details:', error);
      toast.error('Failed to fetch coupon details');
      navigate('/coupons');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductsAndCategories = async () => {
    try {
      const [productsResponse, categoriesResponse] = await Promise.all([
        ProductService.getAllProduct(),
        CategoryService.getAllCategories()
      ]);
      setProducts(productsResponse.data || []);
      setCategories(categoriesResponse.data || []);
    } catch (error) {
      console.error('Error fetching products and categories:', error);
      toast.error('Failed to load products and categories');
    } finally {
      setLoadingData(false);
    }
  };

  const validationSchema = Yup.object({
    couponCode: Yup.string()
      .required('Coupon code is required')
      .min(3, 'Code must be at least 3 characters')
      .max(20, 'Code must be less than 20 characters')
      .matches(/^[A-Z0-9._-]+$/, 'Code must contain only uppercase letters, numbers, periods, hyphens, and underscores'),
    description: Yup.string()
      .max(255, 'Description must be less than 255 characters'),
    discountType: Yup.string().required('Discount type is required'),
    discountValue: Yup.number()
      .required('Discount value is required')
      .positive('Discount value must be positive')
      .when('discountType', {
        is: 'PERCENTAGE',
        then: (schema) => schema.max(100, 'Percentage cannot exceed 100%'),
      }),
    minimumOrderValue: Yup.number()
      .nullable()
      .positive('Minimum order value must be positive'),
    maximumDiscountLimit: Yup.number()
      .nullable()
      .positive('Maximum discount limit must be positive'),
    maxUsagePerUser: Yup.number()
      .nullable()
      .positive('Max usage per user must be positive')
      .integer('Max usage per user must be a whole number'),
    totalMaxUsage: Yup.number()
      .nullable()
      .positive('Total max usage must be positive')
      .integer('Total max usage must be a whole number'),
    validFrom: Yup.date()
      .required('Valid from date is required'),
    validUntil: Yup.date()
      .required('Valid until date is required')
      .min(Yup.ref('validFrom'), 'Valid until date must be after valid from date'),
  });

  const formatDateForInput = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const onSubmit = async (values: any) => {
    console.log('onSubmit called with values:', values);
    setIsSubmitting(true);
    try {
      const couponData = {
        ...values,
        discountValue: parseFloat(values.discountValue),
        minimumOrderValue: values.minimumOrderValue ? parseFloat(values.minimumOrderValue) : null,
        maximumDiscountLimit: values.maximumDiscountLimit ? parseFloat(values.maximumDiscountLimit) : null,
        maxUsagePerUser: values.maxUsagePerUser ? parseInt(values.maxUsagePerUser) : null,
        totalMaxUsage: values.totalMaxUsage ? parseInt(values.totalMaxUsage) : null,
        applicableProductIds: values.applicableProductIds.length > 0 ? values.applicableProductIds : null,
        applicableCategoryIds: values.applicableCategoryIds.length > 0 ? values.applicableCategoryIds : null,
      };

      await CouponService.updateCoupon(parseInt(couponId!), couponData);
      toast.success('Coupon updated successfully');
      navigate(`/coupon/details/${couponId}`);
    } catch (error: any) {
      console.error('Error updating coupon:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update coupon');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <Breadcrumb pageName="Update Coupon" />
        <div className="flex mt-60 items-center justify-center">
          <svg
            className="h-10 w-10 animate-spin text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A8.001 8.001 0 0117.709 7H15v5.373L6.373 17H6v-4.709zM20 12h-4v-.373l-5.373-5.372L15 6.627V2h5.373A8.001 8.001 0 0120 12z"
            ></path>
          </svg>
        </div>
      </>
    );
  }

  if (!coupon) {
    return (
      <>
        <Breadcrumb pageName="Update Coupon" />
        <div className="text-center mt-20">
          <h2 className="text-2xl font-bold text-black dark:text-white mb-4">
            Coupon Not Found
          </h2>
          <button
            onClick={() => navigate('/coupons')}
            className="inline-flex items-center justify-center rounded-md bg-primary py-2 px-4 text-center font-medium text-white hover:bg-opacity-90"
          >
            Back to Coupons
          </button>
        </div>
      </>
    );
  }

  const initialValues = {
    couponCode: coupon.couponCode,
    description: coupon.description || '',
    discountType: coupon.discountType,
    discountValue: coupon.discountValue.toString(),
    minimumOrderValue: coupon.minimumOrderValue?.toString() || '',
    maximumDiscountLimit: coupon.maximumDiscountLimit?.toString() || '',
    maxUsagePerUser: coupon.maxUsagePerUser?.toString() || '',
    totalMaxUsage: coupon.totalMaxUsage?.toString() || '',
    validFrom: formatDateForInput(coupon.validFrom),
    validUntil: formatDateForInput(coupon.validUntil),
    isStackable: coupon.isStackable || false,
    isActive: coupon.isActive,
    applicableProductIds: coupon.applicableProductIds || [],
    applicableCategoryIds: coupon.applicableCategoryIds || [],
  };

  return (
    <>
      <Breadcrumb pageName="Update Coupon" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isValid }) => {
          console.log('Form values:', values);
          console.log('Form errors:', errors);
          console.log('Form touched:', touched);
          console.log('Form isValid:', isValid);
          return (
          <Form>
            <div className="grid grid-cols-1 gap-9 sm:grid-cols-2">
              <div className="flex flex-col gap-9">
                {/* Basic Details */}
                <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                  <div className="border-b border-stroke py-4 px-6.5 dark:border-strokedark">
                    <h3 className="font-medium text-black dark:text-white">
                      Basic Details
                    </h3>
                  </div>
                  <div className="flex flex-col gap-5 p-6.5">
                    <div>
                      <label className="mb-2.5 block text-black dark:text-white">
                        Coupon Code <span className="text-red-500">*</span>
                      </label>
                      <Field
                        type="text"
                        name="couponCode"
                        placeholder="Enter coupon code (e.g., SAVE20)"
                        className="w-full rounded border-[1.5px] border-primary bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary uppercase"
                        style={{ textTransform: 'uppercase' }}
                      />
                      <ErrorMessage
                        name="couponCode"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label className="mb-2.5 block text-black dark:text-white">
                        Description
                      </label>
                      <Field
                        as="textarea"
                        name="description"
                        rows={3}
                        placeholder="Enter coupon description (optional)"
                        className="w-full rounded border-[1.5px] border-primary bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                      />
                      <ErrorMessage
                        name="description"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Optional description to help identify this coupon (max 255 characters)
                      </p>
                    </div>

                    <div>
                      <label className="mb-2.5 block text-black dark:text-white">
                        Status
                      </label>
                      <div className="flex items-center mb-4">
                        <Field
                          type="checkbox"
                          name="isActive"
                          className="mr-2"
                        />
                        <span className="text-black dark:text-white">
                          Active (coupon can be used)
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Field
                          type="checkbox"
                          name="isStackable"
                          className="mr-2"
                        />
                        <span className="text-black dark:text-white">
                          Stackable (can be combined with other coupons)
                        </span>
                      </div>
                    </div>

                    {/* Usage Information */}
                    <div className="border-t border-stroke pt-5 dark:border-strokedark">
                      <h4 className="font-medium text-black dark:text-white mb-3">
                        Usage Information
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-black dark:text-white">Times Used:</span>
                          <span className="ml-2 font-medium text-primary">{coupon.currentUsageCount}</span>
                        </div>
                        <div>
                          <span className="text-black dark:text-white">Created:</span>
                          <span className="ml-2 font-medium">
                            {coupon.createdAt ? new Date(coupon.createdAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-9">
                {/* Discount Details */}
                <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                  <div className="border-b border-stroke py-4 px-6.5 dark:border-strokedark">
                    <h3 className="font-medium text-black dark:text-white">
                      Discount Details
                    </h3>
                  </div>
                  <div className="flex flex-col gap-5 p-6.5">
                    <div>
                      <label className="mb-2.5 block text-black dark:text-white">
                        Discount Type <span className="text-red-500">*</span>
                      </label>
                      <div className="relative z-20 bg-white dark:bg-form-input">
                        <Field
                          as="select"
                          name="discountType"
                          className="relative z-20 w-full appearance-none rounded border border-primary bg-transparent py-1 px-5 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input"
                        >
                          <option value="PERCENTAGE">Percentage (%)</option>
                          <option value="FIXED_AMOUNT">Fixed Amount ($)</option>
                        </Field>
                        <span className="absolute top-1/2 right-4 z-10 -translate-y-1/2">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g opacity="0.8">
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
                                fill="#637381"
                              ></path>
                            </g>
                          </svg>
                        </span>
                      </div>
                      <ErrorMessage
                        name="discountType"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label className="mb-2.5 block text-black dark:text-white">
                        Discount Value <span className="text-red-500">*</span>
                      </label>
                      <Field
                        type="number"
                        name="discountValue"
                        placeholder={values.discountType === 'PERCENTAGE' ? 'Enter percentage (e.g., 20 or 7.5)' : 'Enter amount (e.g., 10.00)'}
                        step="0.01"
                        min="0"
                        max={values.discountType === 'PERCENTAGE' ? '100' : undefined}
                        className="w-full rounded border-[1.5px] border-primary bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                      />
                      <ErrorMessage
                        name="discountValue"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label className="mb-2.5 block text-black dark:text-white">
                        Minimum Order Value
                      </label>
                      <Field
                        type="number"
                        name="minimumOrderValue"
                        placeholder="Enter minimum order value"
                        step="0.01"
                        min="0"
                        className="w-full rounded border-[1.5px] border-primary bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                      />
                      <ErrorMessage
                        name="minimumOrderValue"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    {values.discountType === 'PERCENTAGE' && (
                      <div>
                        <label className="mb-2.5 block text-black dark:text-white">
                          Maximum Discount Limit
                        </label>
                        <Field
                          type="number"
                          name="maximumDiscountLimit"
                          placeholder="Enter maximum discount limit"
                          step="0.01"
                          min="0"
                          className="w-full rounded border-[1.5px] border-primary bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                        />
                        <ErrorMessage
                          name="maximumDiscountLimit"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Applicable Products and Categories */}
            <div className="mt-9">
              <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                <div className="border-b border-stroke py-4 px-6.5 dark:border-strokedark">
                  <h3 className="font-medium text-black dark:text-white">
                    Applicable Products & Categories
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Leave empty to apply to all products. Select specific products or categories to limit coupon usage.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-5 p-6.5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2.5 block text-black dark:text-white">
                      Applicable Products
                    </label>
                    {loadingData ? (
                      <div className="text-sm text-gray-500">Loading products...</div>
                    ) : (
                      <Field name="applicableProductIds">
                        {({ field, form }: any) => (
                          <select
                            multiple
                            className="w-full h-32 rounded border-[1.5px] border-primary bg-transparent py-1 px-3 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                            value={field.value}
                            onChange={(e) => {
                              const selectedValues = Array.from(e.target.selectedOptions, (option: any) => parseInt(option.value));
                              form.setFieldValue(field.name, selectedValues);
                            }}
                          >
                            {products.map((product) => (
                              <option key={product.productId} value={product.productId}>
                                {product.productName}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Hold Ctrl/Cmd to select multiple products
                    </p>
                  </div>

                  <div>
                    <label className="mb-2.5 block text-black dark:text-white">
                      Applicable Categories
                    </label>
                    {loadingData ? (
                      <div className="text-sm text-gray-500">Loading categories...</div>
                    ) : (
                      <Field name="applicableCategoryIds">
                        {({ field, form }: any) => (
                          <div>
                            {/* Selected Categories Display */}
                            {field.value && field.value.length > 0 && (
                              <div className="mb-3">
                                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Selected Categories:</div>
                                <div className="flex flex-wrap gap-2">
                                  {field.value.map((categoryId: number) => {
                                    const category = categories.find(cat => cat.categoryId === categoryId);
                                    return category ? (
                                      <span
                                        key={categoryId}
                                        className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded text-sm"
                                      >
                                        {category.categoryName}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newValues = field.value.filter((id: number) => id !== categoryId);
                                            form.setFieldValue(field.name, newValues);
                                          }}
                                          className="ml-1 text-red-500 hover:text-red-700 font-bold"
                                          title="Remove category"
                                        >
                                          ×
                                        </button>
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {/* Category Selection Dropdown */}
                            <select
                              multiple
                              className="w-full h-32 rounded border-[1.5px] border-primary bg-transparent py-1 px-3 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                              value={field.value}
                              onChange={(e) => {
                                const selectedValues = Array.from(e.target.selectedOptions, (option: any) => parseInt(option.value));
                                form.setFieldValue(field.name, selectedValues);
                              }}
                            >
                              {categories.map((category) => (
                                <option key={category.categoryId} value={category.categoryId}>
                                  {category.categoryName}
                                </option>
                              ))}
                            </select>
                            
                            {/* Clear All Button */}
                            {field.value && field.value.length > 0 && (
                              <button
                                type="button"
                                onClick={() => form.setFieldValue(field.name, [])}
                                className="mt-2 text-sm text-red-500 hover:text-red-700 underline"
                              >
                                Clear All Categories
                              </button>
                            )}
                          </div>
                        )}
                      </Field>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Hold Ctrl/Cmd to select multiple categories, or use the × button to remove individual categories
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Validity and Usage */}
            <div className="mt-9">
              <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                <div className="border-b border-stroke py-4 px-6.5 dark:border-strokedark">
                  <h3 className="font-medium text-black dark:text-white">
                    Validity & Usage
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-5 p-6.5 sm:grid-cols-4">
                  <div>
                    <label className="mb-2.5 block text-black dark:text-white">
                      Valid From <span className="text-red-500">*</span>
                    </label>
                    <Field
                      type="datetime-local"
                      name="validFrom"
                      className="w-full rounded border-[1.5px] border-primary bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    />
                    <ErrorMessage
                      name="validFrom"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label className="mb-2.5 block text-black dark:text-white">
                      Valid Until <span className="text-red-500">*</span>
                    </label>
                    <Field
                      type="datetime-local"
                      name="validUntil"
                      className="w-full rounded border-[1.5px] border-primary bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    />
                    <ErrorMessage
                      name="validUntil"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label className="mb-2.5 block text-black dark:text-white">
                      Max Usage Per User
                    </label>
                    <Field
                      type="number"
                      name="maxUsagePerUser"
                      placeholder="Leave empty for unlimited"
                      min="1"
                      className="w-full rounded border-[1.5px] border-primary bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    />
                    <ErrorMessage
                      name="maxUsagePerUser"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div>
                    <label className="mb-2.5 block text-black dark:text-white">
                      Total Max Usage
                    </label>
                    <Field
                      type="number"
                      name="totalMaxUsage"
                      placeholder="Leave empty for unlimited"
                      min="1"
                      className="w-full rounded border-[1.5px] border-primary bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    />
                    <ErrorMessage
                      name="totalMaxUsage"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                    {coupon.currentUsageCount > 0 && (
                      <p className="text-sm text-warning mt-1">
                        Note: This coupon has already been used {coupon.currentUsageCount} times.
                        Setting a usage limit lower than this will make the coupon unusable.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-4 p-6.5 pt-0">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => console.log('Update Coupon button clicked, isValid:', isValid, 'errors:', errors)}
                    className="flex justify-center rounded bg-primary py-2 px-6 font-medium text-gray hover:bg-opacity-90 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Updating...' : 'Update Coupon'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      console.log('Debug - Current form values:', values);
                      console.log('Debug - Current form errors:', errors);
                      console.log('Debug - Current form touched:', touched);
                      console.log('Debug - Is form valid:', isValid);
                      console.log('Debug - Validation schema:', validationSchema);
                    }}
                    className="flex justify-center rounded bg-secondary py-2 px-6 font-medium text-white hover:bg-opacity-90"
                  >
                    Debug Form
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/coupon/details/${couponId}`)}
                    className="flex justify-center rounded border border-stroke py-2 px-6 font-medium text-black hover:shadow-1 dark:border-strokedark dark:text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </Form>
        );
        }}
      </Formik>
    </>
  );
};

export default UpdateCoupon;